
import React, { useState } from 'react';
import FullscreenLoader from './FullScreenLoader';
import ConfirmationModal from '../modals/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { Order } from '../types';
import { formatCurrency } from '../util/formatCurrency';
import { formatDiscount } from '../util/referralDiscount';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { statusColorMap } from '../util/statusColors';
import { toast } from 'react-toastify';

type OrdersTableProps = {
  orders: Order[];
  showProfitLoss?: boolean;
  openActionMenuId: number | null;
  setOpenActionMenuId: (id: number | null) => void;
  onEdit: (order: Order) => void;
  onDuplicate: (order: Order) => void;
  onSuccess: () => void;
};

// Format a profit/loss amount with the minus sign before the currency symbol (e.g. -E25.00)
const formatProfitLoss = (amount: number | undefined): string => {
  const value = Number(amount) || 0;
  const formatted = `E${Math.abs(value).toFixed(2)}`;
  return value < 0 ? `-${formatted}` : formatted;
};

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  showProfitLoss = false,
  openActionMenuId,
  setOpenActionMenuId,
  onEdit,
  onDuplicate,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [exportingCustomerPdf, setExportingCustomerPdf] = useState(false);
  const [showPdfExportConfirmModal, setShowPdfExportConfirmModal] = useState(false);
  const [showPdfNotificationConfirmModal, setShowPdfNotificationConfirmModal] = useState(false);
  const [selectedOrderForPdf, setSelectedOrderForPdf] = useState<Order | null>(null);

  const handleGenerateInvoice = async (order: Order) => {
    setGeneratingInvoice(true);
    try {
      const res = await fetchWithAuth(`/orders/${order.id}/generate_invoice`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.invoice_url) {
        window.open(data.invoice_url, '_blank');
        onSuccess();
      } else {
        toast.error('Invoice generation failed.');
      }
    } catch (err) {
      toast.error('An error occurred while generating the invoice.');
    } finally {
      setGeneratingInvoice(false);
      setOpenActionMenuId(null);
    }
  };

  const handleExportCustomerPdf = (order: Order) => {
    setSelectedOrderForPdf(order);
    setShowPdfExportConfirmModal(true);
    setOpenActionMenuId(null);
  };

  const handleToggleIsTest = async (order: Order) => {
    setOpenActionMenuId(null);
    const marking = !order.is_test;
    try {
      const res = await fetchWithAuth(`/orders/${order.id}/update_is_test`, {
        method: 'PATCH',
        body: JSON.stringify({ is_test: marking }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.errors?.[0] || 'Failed to update the order.');
        return;
      }
      toast.success(
        marking
          ? `Order ${order.order_reference} is now a test order. Set the Test orders filter to Shown to see it.`
          : `Order ${order.order_reference} is no longer a test order.`
      );
      onSuccess();
    } catch {
      toast.error('Failed to update the order.');
    }
  };

  const handlePdfExportConfirm = () => {
    setShowPdfExportConfirmModal(false);
    setShowPdfNotificationConfirmModal(true);
  };

  const handleFinalPdfConfirm = async (sendNotification: boolean) => {
    setShowPdfNotificationConfirmModal(false);
    if (!selectedOrderForPdf) return;

    setExportingCustomerPdf(true);
    try {
      const res = await fetchWithAuth(`/orders/${selectedOrderForPdf.id}/export_customer_invoice`, {
        method: 'POST',
        body: JSON.stringify({ send_notification: sendNotification }),
      });
      const data = await res.json();
      if (data.message) {
        toast.success(data.message);
        onSuccess();
      } else {
        toast.error('Customer PDF export failed.');
      }
    } catch (err) {
      toast.error('An error occurred while exporting customer PDF.');
    } finally {
      setExportingCustomerPdf(false);
      setSelectedOrderForPdf(null);
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="table-fixed w-full bg-white border border-gray-200 rounded-md shadow-sm">
          <thead className="bg-gray-50 text-sm text-gray-700 font-semibold">
            <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
              <th className="px-2">Order</th>
              <th className="hidden md:table-cell py-2">Date</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Customer</th>
              <th className="hidden md:table-cell px-4 py-2">All in Warehouse</th>
              <th className="hidden md:table-cell px-4 py-2">All in Eswatini</th>
              <th className="hidden md:table-cell px-4 py-2">Doc Fee</th>
              <th className="hidden md:table-cell px-4 py-2">VAT</th>
              <th className="hidden md:table-cell px-4 py-2">Order Items Price</th>
              <th className="hidden md:table-cell px-4 py-2">Customer Self-Purchase</th>
              <th className="hidden md:table-cell px-4 py-2">Delivery Fee</th>
              <th className="px-4 py-2">Total</th>
              {showProfitLoss && <th className="px-4 py-2">Profit / Loss</th>}
              <th className="hidden md:table-cell px-4 py-2">Order Item Costs Confirmed</th>
              <th className="hidden md:table-cell px-4 py-2">Auto Extract</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
        <tbody>
          {orders.map((order) => (
            <tr
              key={order.id}
              className={`border-t border-gray-100 text-sm text-gray-800 relative overflow-visible hover:bg-yellow-100 ${
                openActionMenuId === order.id ? 'bg-yellow-100' : order.has_unpaid_extra_income ? 'bg-red-100' : ''
              }`}
            >
              <td className="px-2">
                <a
                  href={`/orders/${order.id}`}
                  className="text-blue-600 underline"
                >
                  {order.order_reference}
                </a>
                {order.is_test && (
                  <span className="ml-2 inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                    TEST
                  </span>
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2">
                  {new Date(order.date).toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  })}
                </td>
              <td className="px-4 py-2">
                <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColorMap[order.status].class || 'bg-gray-100 text-gray-700'}`}>
                  {order.status.replace(/_/g, ' ')}
                </span>
              </td>
              <td className="px-4 py-2">
                {order.customer ? (
                  <a
                    href={`/customers/${order.customer_id}`}
                    className="text-blue-500 hover:underline"
                  >
                    {order.customer.identifier}
                  </a>
                ) : (
                  <span className="text-gray-500">Unknown customer</span>
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2">
                {order.all_items_in_sa_warehouse ? (
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full" title="Yes"></span>
                ) : (
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full" title="No"></span>
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2">
                {order.all_items_in_eswatini ? (
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full" title="Yes"></span>
                ) : (
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full" title="No"></span>
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2">{formatCurrency(order.documentation_fee)}</td>
              <td className="hidden md:table-cell px-4 py-2">{formatCurrency(order.vat)}</td>
              <td className="hidden md:table-cell px-4 py-2">{formatCurrency(order.order_items_total_price)}</td>
              <td className="hidden md:table-cell px-4 py-2">
                {order.is_customer_purchasing ? (
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full" title="Yes"></span>
                ) : (
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full" title="No"></span>
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2">
                {formatCurrency(order.tini_delivery_fee)}
                {order.referral_discount && (
                  <span
                    className="mt-1 block w-fit px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800"
                    title={`${order.referral_discount.status === 'reserved' ? 'Reserved' : 'Redeemed'} referral discount`}
                  >
                    Referral <span className="whitespace-nowrap">{formatDiscount(order.referral_discount.amount)}</span>
                  </span>
                )}
              </td>
              <td className="px-4 py-2">{formatCurrency(order.total_price)}</td>
              {showProfitLoss && (
                <td className={`px-4 py-2 font-medium ${(order.order_items_profit_loss ?? 0) < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                  {formatProfitLoss(order.order_items_profit_loss)}
                </td>
              )}
              <td className="hidden md:table-cell px-4 py-2">
                {order.order_items_cost_confirmed ? (
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full" title="Yes"></span>
                ) : (
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full" title="No"></span>
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2">
                {order.auto_extraction ? (
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full" title="Yes"></span>
                ) : (
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full" title="No"></span>
                )}
              </td>
              <td className="px-4 py-2 relative overflow-visible">
                <div className="relative inline-block text-left">
                  <button
                    onClick={() => setOpenActionMenuId(openActionMenuId === order.id ? null : order.id)}
                    className="p-1 rounded hover:bg-gray-100"
                    aria-label="Open actions menu"
                  >
                    <span className="text-xl">⋯</span>
                  </button>
                  {openActionMenuId === order.id && (
                    <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded shadow-md z-10">
                      <button
                        onClick={() => {
                          navigate(`/orders/${order.id}`);
                          setOpenActionMenuId(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        View Order
                      </button>
                      <button
                        onClick={() => {
                          onEdit(order);
                          setOpenActionMenuId(null);
                        }}
                        disabled={!order.can_edit}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          order.can_edit
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                        >
                        Edit Order
                      </button>
                      <button
                        onClick={() => {
                          onDuplicate(order);
                          setOpenActionMenuId(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Duplicate Order
                      </button>
                      <button
                        onClick={() => handleGenerateInvoice(order)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Generate Invoice
                      </button>
                      <button
                        onClick={() => {
                          if (order.order_invoice_url) {
                            window.open(order.order_invoice_url, '_blank');
                          }
                          setOpenActionMenuId(null);
                        }}
                        disabled={!order.order_invoice_url}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          order.order_invoice_url
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        View Invoice
                      </button>
                      <button
                        onClick={() => handleExportCustomerPdf(order)}
                        disabled={!order.order_invoice_url}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          order.order_invoice_url
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {order.has_customer_invoice_pdf ? 'Re-export Invoice' : 'Export Invoice'}
                      </button>
                      <button
                        onClick={() => {
                          if (order.slack_order_form_link) {
                            window.open(order.slack_order_form_link, '_blank');
                          }
                          setOpenActionMenuId(null);
                        }}
                        disabled={!order.slack_order_form_link}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          order.slack_order_form_link
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        View Slack
                      </button>
                      <button
                        onClick={() => handleToggleIsTest(order)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                      >
                        {order.is_test ? 'Unmark Test Order' : 'Mark as Test Order'}
                      </button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {generatingInvoice && <FullscreenLoader message="Invoice Generating..." />}
      {exportingCustomerPdf && <FullscreenLoader message="Exporting Customer PDF..." />}

      {showPdfExportConfirmModal && selectedOrderForPdf && (
        <ConfirmationModal
          isOpen={showPdfExportConfirmModal}
          message={`Are you sure you want to ${selectedOrderForPdf.has_customer_invoice_pdf ? 're-export' : 'export'} the customer invoice PDF?`}
          onConfirm={handlePdfExportConfirm}
          onCancel={() => {
            setShowPdfExportConfirmModal(false);
            setSelectedOrderForPdf(null);
          }}
        />
      )}

      {showPdfNotificationConfirmModal && (
        <ConfirmationModal
          isOpen={showPdfNotificationConfirmModal}
          message="Would you like to send a notification?"
          onConfirm={() => handleFinalPdfConfirm(true)}
          onCancel={() => handleFinalPdfConfirm(false)}
        />
      )}
    </>
  );
};

export default OrdersTable;
